import { getPlatformBridge } from './platformBridge'
/** Credential metadata. These stores are app-private, not an OS keychain. */
export class CredentialStorageError extends Error {
  constructor() {
    super('Credentials could not be saved on this device. Free storage space or allow local storage, then try again.')
    this.name = 'CredentialStorageError'
  }
}
function read(key: string): string | null {
  try { return localStorage.getItem(key) } catch { return null }
}
function writeVerified(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
    if (localStorage.getItem(key) !== value) throw new Error('Readback mismatch')
  } catch { throw new CredentialStorageError() }
}
function removeVerified(key: string): void {
  try {
    localStorage.removeItem(key)
    if (localStorage.getItem(key) !== null) throw new Error('Deletion failed')
  } catch { throw new CredentialStorageError() }
}
const REFRESH_TOKEN_KEY = 'superelectric_ink_refresh_token_v1'
const USERNAME_KEY = 'superelectric_ink_username_v1'
const KDF_NONCE_KEY = 'superelectric_ink_kdf_nonce_v1'
const SESSION_ID_KEY = 'superelectric_ink_session_id_v1'
const DEVICE_LABEL_KEY = 'superelectric_ink_device_label_v1'
const ACTIVATION_DEVICE_ID_KEY = 'superelectric_ink_activation_device_id_v1'
const ACTIVATION_CERTIFICATE_KEY = 'superelectric_ink_activation_certificate_v1'
const LOCAL_ACCOUNT_KEY = 'superelectric_ink_local_account_v1'
const CERTIFICATE_BACKUP_KEY = 'superelectric_ink_activation_certificate_backup_v1'
const INSTALLATION_ID_KEY = 'superelectric_ink_installation_id_v1'
const CLOUD_STATE_KEY = 'superelectric_ink_cloud_state_v1'
const LOCAL_LOCK_KEY = 'superelectric_ink_local_lock_v1'
const CREDENTIAL_KEYS = [CLOUD_STATE_KEY, LOCAL_LOCK_KEY, REFRESH_TOKEN_KEY, USERNAME_KEY, KDF_NONCE_KEY, SESSION_ID_KEY, DEVICE_LABEL_KEY, ACTIVATION_DEVICE_ID_KEY, ACTIVATION_CERTIFICATE_KEY, LOCAL_ACCOUNT_KEY, CERTIFICATE_BACKUP_KEY]

export const secureStorage = {
  getCloudState(): string | null { return read(CLOUD_STATE_KEY) },
  setCloudState(reason: string | null): void { if (reason) writeVerified(CLOUD_STATE_KEY, reason); else removeVerified(CLOUD_STATE_KEY) },
  isLocallyLocked(): boolean { return read(LOCAL_LOCK_KEY) === 'locked' },
  setLocallyLocked(locked: boolean): void { if (locked) writeVerified(LOCAL_LOCK_KEY, 'locked'); else removeVerified(LOCAL_LOCK_KEY) },
  snapshot(): Record<string, string | null> {
    return Object.fromEntries(CREDENTIAL_KEYS.map(key => [key, localStorage.getItem(key)]))
  },
  restore(snapshot: Record<string, string | null>): void {
    for (const key of CREDENTIAL_KEYS) {
      const value = snapshot[key]
      if (typeof value === 'string') writeVerified(key, value)
      else removeVerified(key)
    }
  },
  async getRefreshToken(): Promise<string | null> { return read(REFRESH_TOKEN_KEY) },
  async setRefreshToken(value: string): Promise<void> { writeVerified(REFRESH_TOKEN_KEY, value) },
  async clearRefreshToken(): Promise<void> { removeVerified(REFRESH_TOKEN_KEY) },
  async getUsername(): Promise<string | null> { return read(USERNAME_KEY) },
  async setUsername(value: string): Promise<void> { writeVerified(USERNAME_KEY, value) },
  async clearUsername(): Promise<void> { removeVerified(USERNAME_KEY) },
  async getKdfNonce(): Promise<string | null> { return read(KDF_NONCE_KEY) },
  async setKdfNonce(value: string): Promise<void> { writeVerified(KDF_NONCE_KEY, value) },
  async clearKdfNonce(): Promise<void> { removeVerified(KDF_NONCE_KEY) },
  async getSessionId(): Promise<string | null> { return read(SESSION_ID_KEY) },
  async setSessionId(value: string): Promise<void> { writeVerified(SESSION_ID_KEY, value) },
  async clearSessionId(): Promise<void> { removeVerified(SESSION_ID_KEY) },
  async getDeviceLabel(): Promise<string | null> { return read(DEVICE_LABEL_KEY) },
  async setDeviceLabel(value: string): Promise<void> { writeVerified(DEVICE_LABEL_KEY, value) },
  async clearDeviceLabel(): Promise<void> { removeVerified(DEVICE_LABEL_KEY) },
  async getActivationDeviceId(): Promise<string | null> { return read(ACTIVATION_DEVICE_ID_KEY) },
  async setActivationDeviceId(value: string): Promise<void> { writeVerified(ACTIVATION_DEVICE_ID_KEY, value) },
  async clearActivationDeviceId(): Promise<void> { removeVerified(ACTIVATION_DEVICE_ID_KEY) },
  async getActivationCertificate(): Promise<string | null> { return read(ACTIVATION_CERTIFICATE_KEY) },
  async setActivationCertificate(value: string): Promise<void> {
    const previous = read(ACTIVATION_CERTIFICATE_KEY)
    if (previous && previous !== value) writeVerified(CERTIFICATE_BACKUP_KEY, previous)
    writeVerified(ACTIVATION_CERTIFICATE_KEY, value)
  },
  async clearActivationCertificate(): Promise<void> {
    removeVerified(CERTIFICATE_BACKUP_KEY)
    removeVerified(ACTIVATION_CERTIFICATE_KEY)
  },
  async getActivationCertificateBackup(): Promise<string | null> { return read(CERTIFICATE_BACKUP_KEY) },
  async getLocalAccountId(): Promise<string | null> {
    const durable = await getPlatformBridge()?.readLocalAccountId?.()
    return durable ?? read(LOCAL_ACCOUNT_KEY)
  },
  async setLocalAccountId(value: string): Promise<void> {
    await getPlatformBridge()?.bindLocalAccountId?.(value)
    writeVerified(LOCAL_ACCOUNT_KEY, value)
  },
  async getOrCreateInstallationId(): Promise<string> {
    const existing = read(INSTALLATION_ID_KEY)
    const seed = existing ?? crypto.randomUUID()
    const bridge = getPlatformBridge()
    const durable = await bridge?.getInstallationId?.(seed) ?? seed
    if (existing !== durable) {
      try { writeVerified(INSTALLATION_ID_KEY, durable) } catch (error) {
        // Native identity is already durable; an unsuccessful WebView repair
        // must not invalidate a readable Android license during a storage outage.
        if (!('__TAURI_INTERNALS__' in window) || !bridge?.getInstallationId) throw error
        window.dispatchEvent(new CustomEvent('auth:storage-error'))
      }
    }
    return durable
  },
}
